// src/components/Timer/index.js
import React from 'react';
import styled from 'styled-components';
import * as Colors from 'src/styles/colors';

const SECOND = 1000;

const CounterStyle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${Colors.elRed};
  color: #fff;
  padding: 10px 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

function parseTargetDate(targetDate) {
  if (!targetDate) return null;

  const dt =
    typeof targetDate === 'string' || typeof targetDate === 'number'
      ? new Date(targetDate)
      : targetDate;

  const ms = dt?.getTime?.();
  if (!Number.isFinite(ms)) return null;

  return Math.max(0, ms - Date.now());
}

function msToParts(ms) {
  if (!Number.isFinite(ms)) return { d: 0, h: 0, m: 0, s: 0 };

  const total = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;

  return { d, h, m, s };
}

const pad2 = (n) => String(n).padStart(2, '0');

export default function TimeRemaining({
  startDate = null,
  closingAt = null,
  remainingMs = null,
  tickMs = 1000,
}) {
  const targetDate = closingAt || startDate;
  const controlled = Number.isFinite(remainingMs);

  const baseMsRef = React.useRef(null);
  const baseAtRef = React.useRef(null);

  const initial = controlled
    ? Math.max(0, remainingMs)
    : parseTargetDate(targetDate);

  const [displayMs, setDisplayMs] = React.useState(initial);

  React.useEffect(() => {
    if (controlled) {
      const next = Math.max(0, remainingMs);
      baseMsRef.current = next;
      baseAtRef.current = Date.now();
      setDisplayMs(next);
    } else {
      setDisplayMs(parseTargetDate(targetDate));
    }
  }, [controlled, remainingMs, targetDate]);

  React.useEffect(() => {
    const id = setInterval(() => {
      if (controlled) {
        const baseMs = baseMsRef.current;
        const baseAt = baseAtRef.current;

        if (!Number.isFinite(baseMs) || !Number.isFinite(baseAt)) return;

        const elapsed = Date.now() - baseAt;
        setDisplayMs(Math.max(0, baseMs - elapsed));
      } else {
        setDisplayMs(parseTargetDate(targetDate));
      }
    }, tickMs);

    return () => clearInterval(id);
  }, [controlled, targetDate, tickMs]);

  if (!Number.isFinite(displayMs)) {
    return <span style={{ opacity: 0.6 }}>—</span>;
  }

  if (displayMs <= 0) {
    return <CounterStyle>Now!</CounterStyle>;
  }

  const { d, h, m, s } = msToParts(displayMs);
  const urgent = displayMs <= 30 * SECOND;

  const text =
    d > 0
      ? `${d}:${pad2(h)}:${pad2(m)}:${pad2(s)}`
      : h > 0
        ? `${h}:${pad2(m)}:${pad2(s)}`
        : `${pad2(m)}:${pad2(s)}`;

  return (
    <CounterStyle style={{ opacity: urgent ? 0.95 : 1 }}>
      {text}
    </CounterStyle>
  );
}